import React, { useLayoutEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Rnd } from 'react-rnd'
import styled from 'styled-components'

import { Row, Button, Popconfirm, Typography } from 'antd'
import * as Icons from '@ant-design/icons'

import { showRegionEditModal } from './RegionEditModal'

export const Region = ({ moduleName, region, onRegionChanged }) => {
  /** @type {React.MutableRefObject<Rnd>} */
  const ref = useRef()

  const lastRegion = useRef(region)
  useLayoutEffect(() => {
    // We want a semi-controlled component, where we can drive its position to adapt to screen resizing
    //  but we don't need to handle every small motion of moving it around
    //  So we use the default position and update it imperatively when needed, but otherwise let Rnd handle itself

    if (
      lastRegion.current.x !== region.x ||
      lastRegion.current.y !== region.y
    ) {
      ref.current.updatePosition(region)
    }

    if (
      lastRegion.current.height !== region.height ||
      lastRegion.current.width !== region.width
    ) {
      ref.current.updateSize(region)
    }

    lastRegion.current = region
  }, [region])

  async function handleEdit() {
    const nextRegion = await showRegionEditModal(moduleName, region)
    if (nextRegion !== false) {
      onRegionChanged(nextRegion)
    }
  }

  function handleDelete() {
    onRegionChanged(null)
  }

  const regionColour =
    region.field && region.field !== ''
      ? `rgba(0, 200, 255, 0.3)`
      : `rgba(0, 255, 100, 0.3)`

  const StyledRnd = styled(Rnd)`
    transition: all 0.1s ease-in-out;
    transition-property: background-color;

    background-color: ${regionColour};
    :hover {
      background-color: rgba(200, 200, 200, 0.5);
    }
  `

  const NowYouSeeMeText = styled(Typography.Text)`
    margin-right: 5px;
    user-select: none;
    pointer-events: none;

    transition: all 0.1s ease-in-out;
    transition-property: opacity;

    opacity: 0.1;
    ${StyledRnd}:hover & {
      opacity: 1;
    }
  `

  const HiddenButton = styled(Button)`
    transition: all 0.1s ease-in-out;
    transition-property: opacity;

    opacity: 0.1;
    ${StyledRnd}:hover & {
      opacity: 1;
    }
  `
  return (
    <StyledRnd
      ref={ref}
      default={region}
      onDragStop={(e, data) => {
        onRegionChanged({
          ...region,
          x: data.lastX,
          y: data.lastY
        })
      }}
      onResizeStop={(e, dir, element) => {
        onRegionChanged({
          ...region,
          width: element.offsetWidth,
          height: element.offsetHeight
        })
      }}
      style={boxStyle}
      bounds="parent"
      resizeHandleStyles={disableTopRightHandle}
    >
      <Row align="middle" justify="end">
        <NowYouSeeMeText strong>{region.field}</NowYouSeeMeText>

        <HiddenButton
          size="small"
          icon={<Icons.EditFilled />}
          onClick={handleEdit}
          onMouseDown={stopPropagation}
          onMouseMove={stopPropagation}
        />

        <Popconfirm
          title="Delete Region?"
          onConfirm={handleDelete}
          onMouseDown={stopPropagation}
          onMouseMove={stopPropagation}
        >
          <HiddenButton danger size="small" icon={<Icons.DeleteFilled />} />
        </Popconfirm>
      </Row>
    </StyledRnd>
  )
}

Region.propTypes = {
  moduleName: PropTypes.string.isRequired,
  name: PropTypes.string,
  region: PropTypes.shape({
    field: PropTypes.string,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  onRegionChanged: PropTypes.func.isRequired
}

const boxStyle = {
  display: 'flex',
  flexDirection: 'column',
  border: 'solid 1px black'
}

const disableTopRightHandle = {
  topRight: {
    display: 'none'
  }
}

const stopPropagation = e => e.stopPropagation()
